/**
 * Networks Management Page
 * Main page for managing affiliate network connections
 */

import React, { useEffect, useState } from 'react';
import { Plus, RefreshCw, Trash2, Zap } from 'lucide-react';
import { useNetworks, useSyncNetwork, useDeleteNetwork } from '../hooks/useNetworksData';
import { useNetworksStore } from '../stores/networksStore';
import { NetworkCard } from '../components/networks/NetworkCard';
import { FilterBar } from '../components/networks/FilterBar';
import { ConnectionWizard } from '../components/networks/ConnectionWizard';
import { EmptyState } from '../components/networks/EmptyState';
import { NetworkCardSkeletonGrid } from '../components/networks/NetworkCardSkeleton';
import { BulkSyncModal } from '../components/networks/BulkSyncModal';
import { toast } from 'sonner';

export default function Networks() {
  const [isBulkSyncOpen, setIsBulkSyncOpen] = useState(false);
  const { 
    filters, 
    openWizard, 
    selectedNetworks, 
    clearSelection,
    setSyncProgress 
  } = useNetworksStore();
  
  const { data: networks, isLoading, refetch } = useNetworks(filters);
  const syncMutation = useSyncNetwork();
  const deleteMutation = useDeleteNetwork();

  // Auto-refetch every 30 seconds for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30000);

    return () => clearInterval(interval);
  }, [refetch]);

  const handleSyncAll = async () => {
    if (selectedNetworks.length === 0) {
      toast.error('Please select networks to sync');
      return;
    }

    toast.info(`Syncing ${selectedNetworks.length} network(s)...`);

    try {
      // Sync each selected network
      for (const networkId of selectedNetworks) {
        setSyncProgress(networkId, {
          network_id: networkId,
          status: 'syncing',
          progress: 0,
          message: 'Starting sync...',
          started_at: new Date().toISOString()
        });

        await syncMutation.mutateAsync(networkId);

        setSyncProgress(networkId, {
          network_id: networkId,
          status: 'success',
          progress: 100,
          message: 'Sync completed',
          completed_at: new Date().toISOString()
        });
      }

      toast.success('All networks synced successfully');
      clearSelection();
    } catch (error) {
      toast.error('Some networks failed to sync');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedNetworks.length === 0) {
      toast.error('Please select networks to delete');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedNetworks.length} network(s)? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      for (const networkId of selectedNetworks) {
        await deleteMutation.mutateAsync(networkId);
      }

      toast.success(`${selectedNetworks.length} network(s) deleted successfully`);
      clearSelection();
    } catch (error) {
      toast.error('Failed to delete some networks');
    }
  };

  const hasFilters = 
    filters.search !== '' || 
    filters.status !== 'all' || 
    filters.platform !== 'all';

  const allNetworkIds = networks?.map(n => n.id) || [];
  const isAllSelected = selectedNetworks.length > 0 && selectedNetworks.length === allNetworkIds.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      clearSelection();
    } else {
      useNetworksStore.getState().selectAllNetworks(allNetworkIds);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Networks Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Connect and manage your affiliate network accounts
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsBulkSyncOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-500 hover:to-blue-500 transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                aria-label="Bulk sync unsynced accounts"
              >
                <Zap className="w-5 h-5" />
                Bulk Sync
              </button>
            <button
              onClick={openWizard}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              aria-label="Add new network"
            >
              <Plus className="w-5 h-5" />
              Add Network
            </button>
            </div>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selectedNetworks.length > 0 && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                  {selectedNetworks.length} network(s) selected
                </p>
                <button
                  onClick={clearSelection}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline focus:outline-none"
                >
                  Clear selection
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSyncAll}
                  disabled={syncMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                  Sync All
                </button>
                <button
                  onClick={handleDeleteSelected}
                  disabled={deleteMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Selected
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filter Bar */}
        {!isLoading && networks && networks.length > 0 && (
          <FilterBar
            totalNetworks={networks.length}
            filteredNetworks={networks.length}
          />
        )}

        {/* Loading State */}
        {isLoading && <NetworkCardSkeletonGrid count={6} />}

        {/* Empty State */}
        {!isLoading && networks && networks.length === 0 && (
          <EmptyState hasFilters={hasFilters} />
        )}

        {/* Networks Grid */}
        {!isLoading && networks && networks.length > 0 && (
          <>
            {/* Select All Checkbox */}
            <div className="mb-4 flex items-center gap-2">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={toggleSelectAll}
                className="w-4 h-4 text-blue-600 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                aria-label="Select all networks"
              />
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Select all
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {networks.map((network) => (
                <NetworkCard
                  key={network.id}
                  network={network}
                  isSelected={selectedNetworks.includes(network.id)}
                />
              ))}
            </div>
          </>
        )}

        {/* Stats Summary (if networks exist) */}
        {!isLoading && networks && networks.length > 0 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Plus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Networks</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {networks.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <RefreshCw className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Active Networks</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {networks.filter(n => n.sync_status === 'success').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Errors</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {networks.filter(n => n.sync_status === 'error').length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Connection Wizard */}
      <ConnectionWizard />

      {/* Bulk Sync Modal */}
      <BulkSyncModal
        isOpen={isBulkSyncOpen}
        onClose={() => setIsBulkSyncOpen(false)}
        networks={networks?.map(n => ({
          id: n.id,
          account_name: n.account_name,
          platform_name: n.platform_name ?? null,
          platform_provider: n.platform_provider ?? null,
          last_sync_at: n.last_sync_at,
        })) || []}
      />
    </div>
  );
}
